import { z } from "zod"

import {
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const countryCodeSchema = z.string().regex(/^[A-Z]{2}$/)
const stateCodeSchema = z.string().regex(/^[A-Z]{2}$/)
const localityCodeSchema = z.string().regex(/^[A-Z][A-Z0-9_-]{0,31}$/)

export const payrollJurisdictionRuleV1Schema = z.object({
  countryCode: countryCodeSchema,
  stateCode: stateCodeSchema,
  localityCode: localityCodeSchema,
}).strict()

export const payrollJurisdictionPackPublishV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().nonnegative(),
  packId: entityIdSchema,
  revisionId: entityIdSchema,
  packVersion: z.number().int().positive(),
  effectiveFrom: z.iso.date(),
  effectiveThrough: z.iso.date().nullable(),
  rules: z.array(payrollJurisdictionRuleV1Schema).min(1).max(250),
}).strict().superRefine((value, context) => {
  if (
    value.effectiveThrough !== null &&
    value.effectiveThrough < value.effectiveFrom
  ) {
    context.addIssue({
      code: "custom",
      message: "Jurisdiction pack dates are invalid.",
      path: ["effectiveThrough"],
    })
  }
  const keys = value.rules.map(
    (rule) => `${rule.countryCode}:${rule.stateCode}:${rule.localityCode}`,
  )
  if (new Set(keys).size !== keys.length) {
    context.addIssue({
      code: "custom",
      message: "Jurisdiction rules must be unique.",
      path: ["rules"],
    })
  }
})

export const payrollJurisdictionEvaluateV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  evaluationId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  packId: entityIdSchema,
  packVersion: z.number().int().positive(),
  asOfDate: z.iso.date(),
  countryCode: countryCodeSchema.nullable(),
  stateCode: stateCodeSchema.nullable(),
  localityCode: localityCodeSchema.nullable(),
}).strict()

export const payrollJurisdictionDecisionV1Schema = z.object({
  entityType: z.literal("payroll_jurisdiction_evaluation"),
  entityId: entityIdSchema,
  state: z.enum(["supported", "blocked"]),
  version: z.literal(0),
  legalEntityId: legalEntityIdSchema,
  packId: entityIdSchema,
  packVersion: z.number().int().positive(),
  finalizationAllowed: z.boolean(),
  reasonCode: z.enum([
    "supported",
    "pack_unavailable",
    "pack_version_mismatch",
    "outside_effective_window",
    "unknown_country",
    "unsupported_country",
    "unknown_state",
    "unsupported_state",
    "unknown_locality",
    "unsupported_locality",
  ]),
  timePreserved: z.literal(true),
  exportAvailable: z.literal(true),
}).strict()

export type PayrollJurisdictionPackPublishV1 = z.infer<
  typeof payrollJurisdictionPackPublishV1Schema
>
export type PayrollJurisdictionEvaluateV1 = z.infer<
  typeof payrollJurisdictionEvaluateV1Schema
>
export type PayrollJurisdictionDecisionV1 = z.infer<
  typeof payrollJurisdictionDecisionV1Schema
>
